import type { ReactNode } from 'react';

/**
 * A nudge is anything the dashboard proactively puts in front of the user to move the fleet
 * forward. Two kinds, and the distinction is the Cobli vocabulary — not a visual variant:
 *
 * - `recommendation`: goes through an AI workflow, which means it calls a back end. Places
 *   (built from the fleet's most-visited locations) and groups are recommendations.
 * - `suggestion`: deterministic. It may not call a back end at all. The Cobli Conecta download
 *   and the pending-installation scheduling prompt are suggestions.
 */
export type NudgeKind = 'suggestion' | 'recommendation';

/**
 * Where a nudge is rendered. Part of the dismiss key, so dismissing a nudge on one surface does
 * not hide it on the other — closing the floating card must not take the in-page banner with it.
 */
export type NudgeSurface = 'floating-card' | 'banner';

/** Lifecycle callbacks a source gets from `useNudge` and reports the user's actions through. */
export interface NudgeRenderContext {
  dismiss: (reason?: string) => void;
  accept: (metadata?: Record<string, unknown>) => void;
  trackClick: (metadata?: Record<string, unknown>) => void;
}

export interface Nudge {
  /** Stable across renders and releases — it is the analytics key and the dismiss key. */
  id: string;
  kind: NudgeKind;
  /**
   * Tie-break WITHIN a kind (lower first). It never lifts a recommendation above a suggestion:
   * `kind` is the primary sort key. See `compareNudges`.
   */
  priority: number;
  /** Translated label for the card/banner tag. The source owns the copy, the chrome owns the look. */
  tagLabel: string;
  /**
   * Translated one-line title. The chrome needs it as a string because the collapsed row in the
   * floating stack renders it WITHOUT calling `render` — the body stays unmounted while collapsed.
   * Sources pass the same value into their body, so expanding does not change the wording.
   */
  title: string;
  /** Wired to the source's own `useNudge`, so the chrome's close button needs no plumbing. */
  dismiss: (reason?: string) => void;
  /**
   * Called when the user EXPANDS this nudge by clicking its collapsed row.
   *
   * Declaring it also opts the nudge OUT of being auto-expanded: a nudge that defers work is never
   * the one the stack opens on its own, so the deferred work only ever runs on explicit intent.
   * Without that, a page where this is the only nudge would auto-expand it and the deferral would
   * buy nothing — which is exactly how the first attempt leaked `GenerateGroupRecommendation` onto
   * a camera page.
   *
   * The AI recommendation sources gate their `generate` query on this: the stack lives in the app
   * shell, so running the workflow eagerly would fire it on every page of the dashboard — the
   * LLM-cost guardrail that `useAiRecommendationCta` protects by deep-linking instead.
   */
  onExpand?: () => void;
  /**
   * The nudge's body: title, description and its own CTAs. Takes no arguments — the source built
   * it over its own `useNudge` result, so `accept`/`trackClick` are already closed over.
   */
  render: () => ReactNode;
}

/**
 * A source is a HOOK that decides its own eligibility and returns `null` when the fleet should not
 * see it. Sources own their feature flags and thresholds; the container never inspects them, which
 * is what keeps per-source special cases out of the stack.
 */
export type NudgeSource = () => Nudge | null;

const KIND_RANK: Record<NudgeKind, number> = { suggestion: 0, recommendation: 1 };

/**
 * Suggestions always come before recommendations; `priority` only orders peers of the same kind.
 * The pre-AE-2574 floating card sorted by criticality and then spliced the suggestion into index 0
 * as a special case — this comparator makes that ordering the rule instead of the exception.
 */
export const compareNudges = (a: Nudge, b: Nudge): number => KIND_RANK[a.kind] - KIND_RANK[b.kind] || a.priority - b.priority;
